import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button } from '@mui/material';
import {
  GridFilterModel,
  GridLogicOperator,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useMutation, useQueries } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { ACCOUNT_SCOPE_NAMES } from 'common/src/database/scopes/account/accountScopeNames';
import { BILL_SCOPE_NAMES } from 'common/src/database/scopes/bill/billScopeNames';
import { FilterParam } from 'common/src/query-params/FilterParam';
import {
  ArrayOperators,
  SingleOperators,
} from 'common/src/query-params/operators';
import { BASE, ID_PARAMS, NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { GENERATED_ROUTES } from 'common/src/routes/GeneratedRoutes';
import {
  IAccount,
  IBill,
  IFacility,
  IMeter,
  IPage,
  IUtility,
} from 'common/src/types/interfaces';

import { getAccounts } from 'portal/src/api/AccountsService';
import {
  bulkDeleteBills,
  deleteBill,
  getBills,
} from 'portal/src/api/BillsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import { getFacilities } from 'portal/src/api/FacilitiesService';
import { getImportFileDownloadUrl } from 'portal/src/api/ImportFileService';
import { getMeters } from 'portal/src/api/MetersService';
import { getUtilities } from 'portal/src/api/UtilitiesService';
import useAuth from 'portal/src/auth/useAuth';
import type { ExtraActionBtn } from 'portal/src/components/common/tables/addActionColumnProps';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { useTableStateContext } from 'portal/src/providers/TableProviderContext';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import BillsBulkDeletionImpactTable from './BillsBulkDeletionImpactTable';
import buildBillsColumns from './buildBillsColumns';
import { getValidInvalidItems, parseFilterQuery } from './helpers';

import DataTable from '../../common/DataTable';
import SplitButton from '../../common/SplitButton';
import ConfirmDeleteDialog from '../../common/tables/ConfirmDeleteDialog';
import LineItemSubTypeDialogTable from '../Form/Bills/LineItemSubtypes/LineItemSubTypeDialogTable';

interface PageState {
  activeFilters: GridFilterModel;
  page: number;
  pageSize: number;
}

const INITIAL_PAGE_STATE = {
  activeFilters: { items: [] },
  page: 0,
  pageSize: 50,
} as PageState;

const EMPTY_SELECTION = {
  ids: new Set<string>(),
  type: 'include',
} as GridRowSelectionModel;

const Bills = () => {
  const navigate = useNavigate();
  const { hasReadOnlyAccess } = useAuth();
  const snackbarHandler = useSnackbarHandler();
  const { filter: defaultFilter = { items: [] }, sort: defaultSort } =
    useTableStateContext()[NESTED_ROUTES.ADMINISTRATION.BILLS];

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    accountGroup: false,
    billEntryMethod: false,
    correctedBy: false,
    corrections: false,
    date: false,
    department: false,
    id: false,
    image: false,
    serviceType: false,
    statementId: false,
  });
  const [filterModel, setFilterModel] = useState<any>(defaultFilter);
  const [sortModel, setSortModel] = useState(defaultSort);
  const [pageState, setPageState] = useState<PageState>(() => {
    const initialFilters = defaultFilter ?? INITIAL_PAGE_STATE.activeFilters;
    const [validItems] = getValidInvalidItems(initialFilters?.items);

    return {
      ...INITIAL_PAGE_STATE,
      activeFilters: {
        ...initialFilters,
        items: parseFilterQuery({ ...initialFilters, items: validItems }),
      },
    };
  });
  const [currentBills, setCurrentBills] = useState<IPage<IBill>>({
    data: [],
    meta: { totalCount: 0 },
  } as IPage<IBill>);
  const [showLiSubtypeDialog, setLiSubtypeDialog] = useState(false);
  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>(EMPTY_SELECTION);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  const [
    { data: accounts, isFetching: fetchingAccounts },
    { data: facilities, isFetching: fetchingFacilities },
    { data: meters, isFetching: fetchingMeters },
    { data: utilities, isFetching: fetchingUtilities },
    { isError: billsError, isFetching: fetchingBills, refetch: refetchBills },
  ] = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.accounts],
        queryFn: async () =>
          await getAccounts({
            filters: [
              {
                field: 'isActive',
                value: true,
                operator: SingleOperators.EQUALS,
              },
            ],
            scope: ACCOUNT_SCOPE_NAMES.idNumberAndGroupOnly,
          }),
        initialData: { data: [] } as IPage<IAccount>,
      },
      {
        queryKey: ['facilities'],
        queryFn: async () => await getFacilities(),
        initialData: { data: [] } as IPage<IFacility>,
      },
      {
        queryKey: [QUERY_KEYS.meters],
        queryFn: async () =>
          await getMeters({
            filters: [
              {
                field: 'isActive',
                value: true,
                operator: SingleOperators.EQUALS,
              },
            ],
          }),
        initialData: { data: [] } as IPage<IMeter>,
      },
      {
        queryKey: [QUERY_KEYS.utilities],
        queryFn: async () => await getUtilities(),
        initialData: { data: [] } as IPage<IUtility>,
      },
      {
        queryKey: ['bills', pageState, sortModel],
        queryFn: async ({ queryKey }) => {
          const [_, page, sort] = queryKey as [
            string,
            PageState,
            GridSortModel,
          ];
          const activeSort = sort?.length ? sort : defaultSort;

          const response = await getBills({
            limit: page.pageSize,
            page: page.page,
            sort: activeSort[0].field,
            sortDirection: activeSort[0].sort,
            filters: page.activeFilters.items as FilterParam[],
            scope: BILL_SCOPE_NAMES.table,
          });

          // Set state to keep track of the current bills. If we use data returned from the query, we get a new initial page of data for every query key change.
          // A new empty array of bills causes the table to reset it's interal state. When changing pages, this triggers loading the next page but then the table resets
          // and trigger reloading the first page
          setCurrentBills(response);

          return response;
        },
      },
    ],
  });

  const debouncedFetchOnFilterChange = useMemo(() => {
    return debounce((newFilter) => {
      let enableFetch = true;
      let validItems = [];

      if (newFilter) {
        validItems = getValidInvalidItems(newFilter.items)[0];

        enableFetch = !isEqual(validItems, pageState.activeFilters.items);
      }

      if (enableFetch) {
        setPageState((old) => {
          const items = parseFilterQuery({
            ...old.activeFilters,
            items: validItems,
          });

          return {
            ...old,
            page: 0,
            activeFilters: {
              ...old.activeFilters,
              items,
            },
          };
        });
      }
    }, 500);
  }, [pageState]);

  const fetchDataOnFilterChange = useCallback(
    (newFilter) => {
      debouncedFetchOnFilterChange(newFilter);
    },
    [debouncedFetchOnFilterChange],
  );

  const selectedBillIds = useMemo(() => {
    const { ids, type } = selectionModel ?? EMPTY_SELECTION;

    return (currentBills.data ?? [])
      .map(({ id }) => id)
      .filter((id) => (type === 'exclude' ? !ids.has(id) : ids.has(id)));
  }, [currentBills, selectionModel]);

  // Deleting an allocated bill cascades to delete its child allocations; a selected
  // child allocation whose allocated bill isn't also selected is skipped by the server
  const {
    orphanedAllocationBillIds,
    parentBillsSelected,
    regularBillsSelected,
  } = useMemo(() => {
    const selectedIdSet = new Set(selectedBillIds);
    const selectedBills = (currentBills.data ?? []).filter((bill) =>
      selectedIdSet.has(bill.id),
    );

    return {
      orphanedAllocationBillIds: selectedBills
        .filter(
          (bill) =>
            !!bill.masterBillId && !selectedIdSet.has(bill.masterBillId),
        )
        .map(({ id }) => id),
      parentBillsSelected: selectedBills.filter(
        (bill) => !!bill.allocatedBills?.length,
      ),
      regularBillsSelected: selectedBills.filter(
        (bill) => !bill.masterBillId && !bill.allocatedBills?.length,
      ),
    };
  }, [currentBills, selectedBillIds]);

  const bulkDeleteDialogMessage = useMemo(() => {
    const selectedCount = selectedBillIds.length;

    if (
      parentBillsSelected.length === 0 &&
      orphanedAllocationBillIds.length === 0
    ) {
      return selectedCount === 1
        ? 'Are you sure you want to delete this bill?'
        : `Are you sure you want to delete the ${selectedCount} selected bills?`;
    }

    const orphanedIdSet = new Set(orphanedAllocationBillIds);
    const orphanedBillsSelected = (currentBills.data ?? []).filter((bill) =>
      orphanedIdSet.has(bill.id),
    );

    const allocationImpactRows = [
      ...orphanedBillsSelected.map((bill) => ({
        bill,
        willBeDeleted: false,
        note: 'Will not be deleted (allocated bill not selected)',
      })),
      ...parentBillsSelected.map((bill) => {
        const allocationCount = bill.allocatedBills?.length ?? 0;

        return {
          bill,
          willBeDeleted: true,
          note: `Will be deleted with its ${allocationCount} allocation${allocationCount === 1 ? '' : 's'}`,
        };
      }),
      ...regularBillsSelected.map((bill) => ({
        bill,
        willBeDeleted: true,
        note: 'Will be deleted',
      })),
    ];

    return <BillsBulkDeletionImpactTable rows={allocationImpactRows} />;
  }, [
    parentBillsSelected,
    orphanedAllocationBillIds,
    currentBills.data,
    regularBillsSelected,
    selectedBillIds,
  ]);

  const { mutate: deleteSelectedBills, isPending: isBulkDeleting } =
    useMutation({
      mutationFn: async () => await bulkDeleteBills(selectedBillIds),
      onSuccess: async ({ deletedIds }) => {
        setShowBulkDeleteDialog(false);
        setSelectionModel(EMPTY_SELECTION);

        if (deletedIds.length) {
          snackbarHandler.success(
            `${deletedIds.length} bill${deletedIds.length > 1 ? 's' : ''} deleted.`,
          );
          await refetchBills();
        }
      },
      onError: () => {
        setShowBulkDeleteDialog(false);
        setSelectionModel(EMPTY_SELECTION);
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      },
    });

  const hasNeedsReviewFilter = !!filterModel?.items?.some(
    ({ field }) => field === 'needsReview',
  );

  const columns = buildBillsColumns(
    hasNeedsReviewFilter,
    accounts.data,
    facilities.data,
    meters.data,
    utilities.data,
  );

  const options = [
    {
      name: 'Configure line item subtypes',
      onClick: () => setLiSubtypeDialog(true),
      icon: <ConstructionOutlinedIcon fontSize="small" />,
    },
  ];

  const isLoading =
    fetchingBills ||
    fetchingAccounts ||
    fetchingFacilities ||
    fetchingMeters ||
    fetchingUtilities;

  const handleNavigate = (id: string) => navigate(`./${id}`);

  return (
    <>
      <DataTable
        actionsConfig={{
          readActionConfig: {
            onClick: (id) => handleNavigate(id),
          },
          extraActions: (bill: IBill) => {
            const extraActions: ExtraActionBtn[] = [];

            const viewPath = bill.statement?.imageLocation
              ? BASE.IMAGES.STATEMENT.replace(ID_PARAMS.ANY, bill.statement.id)
              : bill.image
                ? BASE.IMAGES.BILL.replace(ID_PARAMS.ANY, bill.id)
                : undefined;

            if (viewPath) {
              extraActions.push({
                icon: <OpenInNewIcon />,
                label: `View ${bill.statement?.imageLocation ? 'Statement' : 'Bill'} Image`,
                onClick: () =>
                  window.open(viewPath, '_blank', 'noopener,noreferrer'),
              });
            }

            if (bill.correctedBy) {
              extraActions.push({
                icon: <HistoryIcon />,
                label: 'View Correction',
                onClick: () =>
                  window.open(
                    `${GENERATED_ROUTES.ADMINISTRATION.BILLS}/${bill.correctedBy}`,
                    '_blank',
                    'noopener,noreferrer',
                  ),
              });
            }

            if (bill.importFileId) {
              extraActions.push({
                icon: <DownloadIcon />,
                label: 'Download Spreadsheet File',
                downloadHref: getImportFileDownloadUrl(bill.importFileId),
              });
            }

            return extraActions;
          },
          deleteConfig: {
            targetStr: 'bill',
            isDisabledForRow: (row: IBill) => {
              return !!row.masterBillId;
            },
            onDeleteClick: async (id) => {
              await deleteBill(id);
              await refetchBills();
            },
            snackbarMsgKey: 'BILL',
            message: (bill) =>
              `Are you sure you want to delete this bill${
                bill?.allocatedBills?.length
                  ? ' along with its allocations'
                  : ''
              }?`,
          },
        }}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        contextName={NESTED_ROUTES.ADMINISTRATION.BILLS}
        filterModel={filterModel}
        dataFetchError={billsError}
        disableColumnFilter={isLoading}
        filterMode="server"
        loading={isLoading}
        onFilterModelChange={(value) => {
          if (
            !!filterModel &&
            JSON.stringify(filterModel.items) === JSON.stringify(value.items)
          ) {
            return;
          }

          const newFilterItems = value.items.map((item, idx) => {
            const oldItem = filterModel?.items[idx] ?? {};
            if (
              oldItem?.field !== item.field &&
              [
                'accountGroup',
                'accountId',
                'facilityId',
                'image',
                'meterId',
                'serviceType',
                'utilityId',
              ].includes(item.field)
            ) {
              return {
                ...item,
                operator: ArrayOperators.IN,
                value: [],
              };
            }
            return item;
          });

          const newFilter = { ...value, items: newFilterItems };
          setFilterModel(newFilter);
          fetchDataOnFilterChange(newFilter);
        }}
        onPaginationModelChange={(model, { reason }) => {
          if (reason === 'setPaginationModel') {
            setPageState((old) => {
              const page = model.pageSize !== old.pageSize ? 0 : model.page;

              return {
                ...old,
                page,
                pageSize: model.pageSize,
              };
            });
          }
        }}
        onSortModelChange={(model) => {
          const nextSort = model?.length ? model : defaultSort;
          setSortModel(nextSort);

          setPageState((old) => {
            return {
              ...old,
              page: 0,
            };
          });
        }}
        onRowSelectionModelChange={setSelectionModel}
        rowSelectionModel={selectionModel}
        paginationMode="server"
        paginationModel={{
          page: pageState.page,
          pageSize: pageState.pageSize,
        }}
        rows={currentBills.data || []}
        rowCount={currentBills.meta.totalCount}
        sortingMode="server"
        slots={{
          ...(fetchingBills && {
            pagination: () => {
              return (
                <Box sx={{ marginRight: '15px', fontSize: '1.2em' }}>
                  loading...
                </Box>
              );
            },
          }),
        }}
        slotProps={{
          filterPanel: {
            logicOperators: [GridLogicOperator.And],
            filterFormProps: {
              // correctedBy is only filtered by links from the bill detail page
              filterColumns: ({ field, columns }) =>
                columns
                  .filter(
                    (column) =>
                      column.filterable &&
                      (column.field !== 'correctedBy' ||
                        field === 'correctedBy'),
                  )
                  .map((column) => column.field),
            },
          },
        }}
        toolbarSlotProps={{
          children: (
            <SplitButton
              disabled={hasReadOnlyAccess()}
              name="New"
              endIcon={<NoteAddOutlinedIcon />}
              onClick={() => handleNavigate(NEW_ID)}
              options={options}
            />
          ),
          exportFileName: 'bills',
          leftSideChildren: (
            <Button
              color="error"
              disabled={
                hasReadOnlyAccess() || !selectedBillIds.length || isBulkDeleting
              }
              onClick={() => setShowBulkDeleteDialog(true)}
              size="small"
              startIcon={<DeleteOutlineIcon sx={{ mb: '3px' }} />}
              sx={{ ml: 1 }}
              variant="outlined"
            >
              Delete Selected
              {selectedBillIds.length ? ` (${selectedBillIds.length})` : ''}
            </Button>
          ),
          showColumns: true,
          showFilters: true,
        }}
      />
      <ConfirmDeleteDialog
        maxWidth="lg"
        isLoading={isBulkDeleting}
        message={bulkDeleteDialogMessage}
        onClose={() => setShowBulkDeleteDialog(false)}
        onConfirm={() => deleteSelectedBills()}
        showDialog={showBulkDeleteDialog}
        disabled={orphanedAllocationBillIds.length === selectedBillIds.length}
      />
      <LineItemSubTypeDialogTable
        open={showLiSubtypeDialog}
        onClose={() => setLiSubtypeDialog(false)}
      />
    </>
  );
};

export default Bills;
